import { Container, Sprite } from '../../odie';

import { app } from '../app';
import { IAnimation } from '../../lib/pattern/IAnimation';
import { getMatch2AssetPath } from '../util/assets';
import { ParticleEmitter } from '../particles/ParticleEmitter';
import { gsap } from 'gsap';
import { tweenPower2In } from '../../lib/util/tweens';
import { DynamicXy } from '../../lib/defs/types';

// types
//-----------------------------------------------------------------------------
export type SplashAnimationOptions = {
  assets: string[];
  tint: number;
  amount: number;
  scale: DynamicXy;
};

const defaultOptions: SplashAnimationOptions = {
  assets: [getMatch2AssetPath('fx.basic/fx.basic.splash.png')],
  tint: 0xffffff,
  amount: 4,
  scale: { x: 1, y: 1 },
};

/*
    smoke animation
*/
export class SplashAnimation extends Container implements IAnimation {
  // fields
  //-------------------------------------------------------------------------
  private _options: SplashAnimationOptions;

  // init
  //-------------------------------------------------------------------------
  constructor(options?: Partial<SplashAnimationOptions>) {
    super();
    this._options = {
      ...defaultOptions,
      ...options,
      assets: options?.assets ?? defaultOptions.assets,
    };
  }

  // impl
  //-------------------------------------------------------------------------
  public async start(): Promise<void> {
    // create splash emitter
    const splashEmitter = this._createSplashEmitter();

    // create blast
    const blast = this._createBlast();

    // animate splash and blast
    await Promise.all([
      this._animateSplash(splashEmitter),
      this._animateBlast(blast),
    ]);
  }

  public stop() {}

  // private: animators
  //-------------------------------------------------------------------------
  private async _animateSplash(splash: ParticleEmitter) {
    // spawn debris
    this.addChild(splash.view);

    // start
    await splash.start();

    // despawn blast
    this.removeChild(splash.view);
  }

  private async _animateBlast(blast: Sprite) {
    // set initial values
    blast.scaleX = 0.2;
    blast.scaleY = 0.2;

    // spawn blast
    this.addChild(blast);

    // animate
    await gsap.to(blast, {
      scaleX: 1.6,
      scaleY: 1.6,
      duration: 0.2,
      ease: 'power1.out',
    });

    // despawn blast
    this.removeChild(blast);
  }

  // private: factory
  //-------------------------------------------------------------------------
  private _createSplashEmitter(): ParticleEmitter {
    return app.particles.create({
      textures: this._options.assets,
      rate: 0,
      limit: this._options.amount,
      emitterDuration: 0,
      duration: 1,
      velocity: { x: 0, y: -200 },
      scale: this._options.scale,
      behaviors: [
        { type: 'explode', magnitude: [250, 350] },
        { type: 'gravity', gravity: { x: 0, y: 1400 } },
        { type: 'scale', to: 0, tween: tweenPower2In, mirrorX: true },
        {
          type: 'kinematic',
          directionalRotation: true,
          rotationOffset: -Math.PI * 0.5,
        },
      ],
    });
  }

  private _createBlast(): Sprite {
    const blast = Sprite.from('fx.block/fx.block.explode.png');
    blast.anchorX = 0.5;
    blast.anchorY = 0.5;
    return blast;
  }
}
